/**
 * Port of ryos `SeekBar.tsx` (behavior and structure preserved; Tailwind → styled-components).
 * Source: https://github.com/ryokun6/ryos/blob/main/src/apps/videos/components/SeekBar.tsx (AGPL-3.0)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const OuterMotion = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 12rem;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.2) 50%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 3;
`;

const InnerPad = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
`;

const BarTrack = styled.div`
  position: relative;
  height: 8px;
  border-radius: 9999px;
  cursor: pointer;
  pointer-events: auto;
  overflow: hidden;
  transition: border 150ms ease, background 150ms ease;
  user-select: none;
  touch-action: none;
  border: 1px solid
    ${({ $dragging }) =>
      $dragging ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.5)'};
  background: ${({ $dragging }) =>
    $dragging ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)'};
  -webkit-user-select: none;
  -webkit-touch-callout: none;
`;

const Fill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  transition: background-color 150ms ease, width 150ms ease;
  background: ${({ $dragging }) =>
    $dragging ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.5)'};
  width: ${({ $pct }) => $pct}%;
`;

export default function RyosSeekBar({
  duration,
  currentTime,
  onSeek,
  isPlaying,
  isHovered: parentHovered,
  onDragChange,
  autoDismissTimeout = 3000,
}) {
  const [isLocalHovered, setIsLocalHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const seekBarRef = useRef(null);
  const autoDismissTimerRef = useRef(null);

  const progress =
    duration > 0 && Number.isFinite(currentTime)
      ? (currentTime / duration) * 100
      : 0;

  useEffect(() => {
    if (!isDragging) {
      setSeekPosition(progress);
    }
  }, [currentTime, duration, isDragging, progress]);

  const startAutoDismissTimer = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
    }

    if (isPlaying && !isDragging) {
      autoDismissTimerRef.current = setTimeout(() => {
        setIsVisible(false);
      }, autoDismissTimeout);
    }
  }, [isPlaying, isDragging, autoDismissTimeout]);

  const clearAutoDismissTimer = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  }, []);

  const showSeekBar = useCallback(() => {
    setIsVisible(true);
    clearAutoDismissTimer();
    startAutoDismissTimer();
  }, [clearAutoDismissTimer, startAutoDismissTimer]);

  useEffect(() => {
    if (isPlaying && (parentHovered || isLocalHovered || isDragging)) {
      setIsVisible(true);
      clearAutoDismissTimer();
      if (!isDragging) {
        startAutoDismissTimer();
      }
    } else if (!isPlaying) {
      setIsVisible(false);
      clearAutoDismissTimer();
    }
  }, [
    isPlaying,
    parentHovered,
    isLocalHovered,
    isDragging,
    clearAutoDismissTimer,
    startAutoDismissTimer,
  ]);

  useEffect(() => {
    if (isVisible && !isDragging && isPlaying) {
      clearAutoDismissTimer();
      startAutoDismissTimer();
    }
  }, [
    isVisible,
    isDragging,
    isPlaying,
    clearAutoDismissTimer,
    startAutoDismissTimer,
  ]);

  useEffect(() => {
    return () => {
      clearAutoDismissTimer();
    };
  }, [clearAutoDismissTimer]);

  const calculatePosition = useCallback(clientX => {
    if (!seekBarRef.current) return 0;
    const rect = seekBarRef.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    return Math.max(0, Math.min(100, position));
  }, []);

  const handleMouseDown = e => {
    e.stopPropagation();
    setIsDragging(true);
    showSeekBar();
    const position = calculatePosition(e.clientX);
    setSeekPosition(position);
    if (onDragChange) {
      const seekTime = (position / 100) * duration;
      onDragChange(true, seekTime);
    }
  };

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      onSeek((seekPosition / 100) * duration);
      setIsDragging(false);
      if (onDragChange) {
        onDragChange(false);
      }
      startAutoDismissTimer();
    }
  }, [
    isDragging,
    onSeek,
    seekPosition,
    duration,
    onDragChange,
    startAutoDismissTimer,
  ]);

  const handleTouchStart = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    showSeekBar();
    const touch = e.touches[0];
    const position = calculatePosition(touch.clientX);
    setSeekPosition(position);
    if (onDragChange) {
      const seekTime = (position / 100) * duration;
      onDragChange(true, seekTime);
    }
  };

  const handleTouchMove = e => {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    const position = calculatePosition(touch.clientX);
    setSeekPosition(position);
    if (onDragChange) {
      const seekTime = (position / 100) * duration;
      onDragChange(true, seekTime);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      onSeek((seekPosition / 100) * duration);
      setIsDragging(false);
      if (onDragChange) {
        onDragChange(false);
      }
      startAutoDismissTimer();
    }
  };

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseUp = () => {
        handleMouseUp();
      };

      const handleGlobalMouseMove = e => {
        if (!isDragging) return;
        const position = calculatePosition(e.clientX);
        setSeekPosition(position);
        if (onDragChange) {
          const seekTime = (position / 100) * duration;
          onDragChange(true, seekTime);
        }
      };

      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('mousemove', handleGlobalMouseMove);

      return () => {
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('mousemove', handleGlobalMouseMove);
      };
    }
  }, [
    isDragging,
    duration,
    onSeek,
    onDragChange,
    calculatePosition,
    handleMouseUp,
  ]);

  if (!isPlaying) return null;

  return (
    <OuterMotion
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : 20,
      }}
      transition={{
        duration: 0.2,
        ease: 'easeOut',
      }}
      onMouseEnter={() => {
        setIsLocalHovered(true);
        showSeekBar();
      }}
      onMouseLeave={() => {
        setIsLocalHovered(false);
        if (!isDragging) {
          startAutoDismissTimer();
        }
      }}
    >
      <InnerPad>
        <BarTrack
          ref={seekBarRef}
          $dragging={isDragging}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Fill
            $dragging={isDragging}
            $pct={isDragging ? seekPosition : progress}
          />
        </BarTrack>
      </InnerPad>
    </OuterMotion>
  );
}
